import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import { ModalShell } from '../ui/ModalShell';
import { Button } from '../ui/Button';
import { useUpdateMobilisation } from '../../hooks/useMobilisations';
import { ApiError } from '../../lib/api';

export interface MobilisationEditModalProps {
  /** null = closed. */
  mobilisation: { id: string; name: string; date: string } | null;
  onClose: () => void;
}

/**
 * Rename / re-date a mobilisation. Status has its own inline select on
 * the row; attendees have their own panel. Keyed remount (see usage)
 * seeds the fields fresh for each row the modal opens on.
 */
export function MobilisationEditModal({
  mobilisation,
  onClose,
}: MobilisationEditModalProps) {
  const titleId = 'mobilisation-edit-title';
  const update = useUpdateMobilisation();
  const [name, setName] = useState(mobilisation?.name ?? '');
  const [date, setDate] = useState(mobilisation?.date ?? '');

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!mobilisation) return;
    try {
      await update.mutateAsync({ id: mobilisation.id, name: name.trim(), date });
      toast.success(`Saved "${name.trim()}"`);
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'save failed');
    }
  }

  return (
    <ModalShell
      open={!!mobilisation}
      onClose={onClose}
      titleId={titleId}
      busy={update.isPending}
    >
      <form onSubmit={onSubmit} className="p-5">
        <h2 id={titleId} className="text-base font-semibold">
          Edit mobilisation
        </h2>
        <label className="mt-4 block text-sm">
          <span className="font-medium text-fg-muted">Name</span>
          <input
            type="text"
            required
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 block h-10 w-full rounded-md border border-line-strong/20 bg-surface px-3 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
          />
        </label>
        <label className="mt-3 block text-sm">
          <span className="font-medium text-fg-muted">Date</span>
          <input
            type="date"
            required
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="mt-1 block h-10 w-full rounded-md border border-line-strong/20 bg-surface px-3 text-sm shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15"
          />
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="ghost" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="md"
            loading={update.isPending}
            iconLeft={<Save className="h-4 w-4" />}
          >
            Save
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}
